'use client'

import { useState, useEffect } from 'react'
import { Input } from '@/components/ui/input'

interface TaxCalculatorInputProps {
    label: string
    value: number
    onChange: (value: number) => void
    placeholder?: string
    helperText?: string
    helperIcon?: React.ReactNode
    showHelperText?: boolean
    className?: string
}

const formatNumber = (value: number) => {
    return new Intl.NumberFormat('en-US', { maximumFractionDigits: 3 }).format(value)
}

export default function TaxCalculatorInput({
    label,
    value,
    onChange,
    placeholder = '',
    helperText,
    helperIcon,
    showHelperText = true,
    className = '',
}: TaxCalculatorInputProps) {
    const [inputStr, setInputStr] = useState(formatNumber(value))

    // Sync inputStr when value changes externally
    useEffect(() => {
        setInputStr(formatNumber(value))
    }, [value])

    const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        const rawValue = e.target.value.replace(/,/g, '')
        if (rawValue === '' || rawValue === '.' || !isNaN(Number(rawValue))) {
            setInputStr(e.target.value)
            const numValue = rawValue === '' || rawValue === '.' ? 0 : Number(rawValue)
            onChange(numValue)
        }
    }

    const handleBlur = () => {
        setInputStr(formatNumber(value))
    }

    return (
        <div className={className}>
            <label className="block text-sm font-medium text-gray-700 mb-1.5">
                {label}
            </label>
            <Input
                type="text"
                value={inputStr}
                onChange={handleChange}
                onBlur={handleBlur}
                placeholder={placeholder}
            />
            {showHelperText && helperText && (
                <p className="mt-1.5 text-xs text-gray-500 flex items-start gap-1.5">
                    {helperIcon && <span className="text-gray-400 mt-0.5">{helperIcon}</span>}
                    {helperText}
                </p>
            )}
        </div>
    )
}
